import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Lock, MessageSquareText, Phone, Inbox, Coins, Users, BarChart3, Star, Image as ImageIcon, Sparkles, Copy, Calendar, Plus, Trash2, CheckCircle2, ExternalLink, Film, Search, Settings2, Play, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    API,
    adminClient,
    getAdminToken,
    setAdminToken,
    clearAdminToken,
} from "@/lib/api";
import axios from "axios";
import useSEO from "@/hooks/useSEO";
import SiteEditor from "@/components/admin/SiteEditor";
import ReelPreviewModal from "@/components/admin/ReelPreviewModal";
import VideoStudio from "@/components/admin/VideoStudio";
import VideoEnhancer from "@/components/admin/VideoEnhancer";
import FileConverter from "@/components/admin/FileConverter";
import { MediaDropzone } from "@/components/admin/MediaDropzone";

const STATUS_LABELS = {
    pending: { t: "Pending", c: "bg-zinc-700 text-zinc-100" },
    quote_requested: { t: "Quote Requested", c: "bg-amber-600/30 text-amber-300 border border-amber-600/50" },
    completed: { t: "Job Completed", c: "bg-blue-600/30 text-blue-300 border border-blue-600/50" },
    paid: { t: "Paid", c: "bg-green-600/30 text-green-300 border border-green-600/50" },
};

function StatusPill({ status }) {
    const meta = STATUS_LABELS[status] || STATUS_LABELS.pending;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest ${meta.c}`}>
            {meta.t}
        </span>
    );
}

export function LoginScreen({ onLogin }) {
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/admin/login`, { password });
            setAdminToken(res.data.token);
            onLogin();
            toast.success("Welcome back.");
        } catch {
            toast.error("Wrong password.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="lbp-container py-20 max-w-md">
            <div className="rounded-sm border border-white/10 bg-zinc-900 p-8">
                <Lock className="w-7 h-7 text-red-500" />
                <h1 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl">
                    Admin Login
                </h1>
                <p className="mt-2 text-sm text-zinc-400">
                    Internal dashboard — leads, referrals, and Rev Cash.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                            Password
                        </Label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-password-input"
                            className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white h-11"
                            autoFocus
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub }) {
    return (
        <div className="rounded-sm border border-white/10 bg-zinc-900 p-5">
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-red-500" />
                <span className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                    {label}
                </span>
            </div>
            <div className="mt-3 font-display font-black text-white text-3xl">{value}</div>
            {sub ? <div className="mt-1 text-xs text-zinc-500">{sub}</div> : null}
        </div>
    );
}

function PhotoStudio() {
    const [folder, setFolder] = useState("gallery");
    const [slug, setSlug] = useState("");
    const [prompt, setPrompt] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const fullUrl = result ? `${API.replace(/\/api$/, "")}${result.url}` : "";

    const generate = async (e) => {
        e.preventDefault();
        const cleanSlug = slug
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
        if (cleanSlug.length < 2) return toast.error("Pick a slug (e.g. clean-mustang-driveway).");
        if (prompt.trim().length < 8) return toast.error("Give a longer description.");

        setBusy(true);
        setResult(null);
        try {
            const res = await adminClient().post("/admin/generate-image", {
                folder,
                slug: cleanSlug,
                prompt: prompt.trim(),
            });
            setResult(res.data);
            toast.success("Photo generated.");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Generation failed — try again.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const copyUrl = async () => {
        if (!fullUrl) return;
        try {
            await navigator.clipboard.writeText(fullUrl);
            toast.success("URL copied.");
        } catch {
            toast.error("Copy failed — long-press to copy.");
        }
    };

    return (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <form
                onSubmit={generate}
                className="rounded-sm border border-white/10 bg-zinc-900 p-5 sm:p-6 space-y-5"
                data-testid="admin-photo-studio-form"
            >
                <div>
                    <h3 className="font-display font-black uppercase tracking-tight text-white text-xl flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-red-500" /> Photo Studio
                    </h3>
                    <p className="mt-1 text-sm text-zinc-400">
                        Type one line about the shot you want and this'll generate a photo-realistic image
                        for the site. Lives at <code className="text-red-400">/api/static/&lt;folder&gt;/&lt;slug&gt;.png</code>.
                    </p>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-400">Where it goes</Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                        {[
                            { v: "gallery", t: "Gallery cover" },
                            { v: "services", t: "Service photo" },
                            { v: "articles", t: "Article cover" },
                        ].map((opt) => (
                            <button
                                type="button"
                                key={opt.v}
                                onClick={() => setFolder(opt.v)}
                                data-testid={`photo-studio-folder-${opt.v}`}
                                className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border ${
                                    folder === opt.v
                                        ? "border-red-600 bg-red-600/10 text-white"
                                        : "border-white/10 text-zinc-400 hover:text-white"
                                }`}
                            >
                                {opt.t}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <Label htmlFor="ps-slug" className="text-xs uppercase tracking-widest text-zinc-400">
                        Slug <span className="text-zinc-600">(filename, lowercase-dashes)</span>
                    </Label>
                    <Input
                        id="ps-slug"
                        data-testid="photo-studio-slug"
                        value={slug}
                        onChange={(ev) => setSlug(ev.target.value)}
                        placeholder="clean-mustang-driveway"
                        className="mt-2 bg-zinc-950 border-white/10 text-white"
                    />
                </div>

                <div>
                    <Label htmlFor="ps-prompt" className="text-xs uppercase tracking-widest text-zinc-400">
                        Describe the photo
                    </Label>
                    <textarea
                        id="ps-prompt"
                        data-testid="photo-studio-prompt"
                        value={prompt}
                        onChange={(ev) => setPrompt(ev.target.value)}
                        rows={4}
                        placeholder="Clean white Mustang GT parked in a suburban driveway at golden hour, three-quarter front view."
                        className="mt-2 w-full bg-zinc-950 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600"
                    />
                    <p className="mt-1.5 text-[11px] text-zinc-500">
                        Style (photo-realistic, 16:9, no text, no faces) is added automatically.
                    </p>
                </div>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="photo-studio-generate"
                    className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider px-5 py-3 rounded-sm flex items-center justify-center gap-2"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {busy ? "Generating…" : "Generate Photo"}
                </button>
            </form>

            <div className="rounded-sm border border-white/10 bg-zinc-900 p-5 sm:p-6">
                <h4 className="font-display font-bold uppercase tracking-tight text-white text-base flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-zinc-400" /> Result
                </h4>
                {!result && !busy && (
                    <div className="mt-4 aspect-video rounded-sm border border-dashed border-white/10 bg-zinc-950 flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
                        No photo yet — fill in the form
                    </div>
                )}
                {busy && (
                    <div className="mt-4 aspect-video rounded-sm border border-white/10 bg-zinc-950 flex items-center justify-center text-zinc-500">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                )}
                {result && (
                    <div className="mt-4 space-y-4">
                        <img
                            src={fullUrl}
                            alt={result.slug}
                            data-testid="photo-studio-result-img"
                            className="w-full rounded-sm border border-white/10"
                        />
                        <div className="text-xs text-zinc-400 space-y-1.5">
                            <div>
                                <span className="text-zinc-500">Folder:</span>{" "}
                                <span className="text-white font-semibold">{result.folder}</span>
                            </div>
                            <div>
                                <span className="text-zinc-500">Slug:</span>{" "}
                                <span className="text-white font-semibold">{result.slug}</span>
                            </div>
                            <div className="break-all">
                                <span className="text-zinc-500">Path:</span>{" "}
                                <code className="text-red-400">{result.url}</code>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={copyUrl}
                                data-testid="photo-studio-copy-url"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-zinc-200 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                            >
                                <Copy className="w-3.5 h-3.5" /> Copy full URL
                            </button>
                            <a
                                href={fullUrl}
                                download={`${result.slug}.png`}
                                data-testid="photo-studio-download"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-zinc-200 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                            >
                                Download
                            </a>
                        </div>
                        <p className="text-[11px] text-zinc-500 leading-relaxed">
                            To use it in code: paste the path above into{" "}
                            <code>/app/frontend/src/lib/site.js</code> as the <code>cover</code> /{" "}
                            <code>image</code> field on a job, service, or article. The frontend will pick
                            up the new image instantly via hot reload.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function ContentQueue() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null); // post object when editing

    // Form state
    const empty = {
        caption: "",
        image_url: "",
        scheduled_at: "",
        platforms: [],
        notes: "",
    };
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);
    const [suggestBusy, setSuggestBusy] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [jobSummary, setJobSummary] = useState("");

    const refresh = async () => {
        setLoading(true);
        try {
            const res = await adminClient().get("/admin/posts");
            setPosts(res.data.posts || []);
        } catch {
            toast.error("Couldn't load queue.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const togglePlatform = (p) =>
        setForm((f) => ({
            ...f,
            platforms: f.platforms.includes(p)
                ? f.platforms.filter((x) => x !== p)
                : [...f.platforms, p],
        }));

    const startCreate = () => {
        setEditing(null);
        // Sensible default: tomorrow 10:00 local
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(10, 0, 0, 0);
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        setForm({ ...empty, scheduled_at: local });
        setShowForm(true);
    };

    const startEdit = (p) => {
        setEditing(p);
        const d = new Date(p.scheduled_at + (p.scheduled_at.endsWith("Z") ? "" : "Z"));
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        setForm({
            caption: p.caption,
            image_url: p.image_url || "",
            scheduled_at: local,
            platforms: p.platforms || [],
            notes: p.notes || "",
        });
        setShowForm(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.caption.trim()) return toast.error("Add a caption.");
        if (form.platforms.length === 0) return toast.error("Pick at least one platform.");
        if (!form.scheduled_at) return toast.error("Pick a date/time.");
        const iso = new Date(form.scheduled_at).toISOString();
        const payload = { ...form, scheduled_at: iso };
        setSubmitting(true);
        try {
            if (editing) {
                await adminClient().put(`/admin/posts/${editing.id}`, payload);
                toast.success("Post updated.");
            } else {
                await adminClient().post("/admin/posts", payload);
                toast.success("Post queued.");
            }
            setShowForm(false);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSubmitting(false);
        }
    };

    const markPosted = async (id) => {
        try {
            await adminClient().put(`/admin/posts/${id}`, { status: "posted" });
            toast.success("Marked as posted.");
            refresh();
        } catch {
            toast.error("Couldn't update.");
        }
    };

    const cancelPost = async (id) => {
        if (!window.confirm("Delete this scheduled post?")) return;
        try {
            await adminClient().delete(`/admin/posts/${id}`);
            toast.success("Deleted.");
            refresh();
        } catch {
            toast.error("Couldn't delete.");
        }
    };

    const copyCaption = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Caption copied — paste it on Instagram/TikTok/Facebook.");
        } catch {
            toast.error("Copy failed.");
        }
    };

    const suggestCaptions = async () => {
        const summary = jobSummary.trim();
        if (summary.length < 4) return toast.error("Describe the job briefly first.");
        const plats = form.platforms.length ? form.platforms : ["instagram"];
        setSuggestBusy(true);
        setSuggestions([]);
        try {
            const res = await adminClient().post("/admin/suggest-captions", {
                job_summary: summary,
                platforms: plats,
            });
            setSuggestions(res.data.options || []);
            if ((res.data.options || []).length === 0) toast.error("No options returned.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't generate captions.");
        } finally {
            setSuggestBusy(false);
        }
    };

    const applySuggestion = (text) => {
        setForm((f) => ({ ...f, caption: text }));
        setSuggestions([]);
        toast.success("Caption loaded — tweak if you want, then queue it.");
    };

    const STATUS_STYLE = {
        queued: "bg-zinc-700 text-zinc-100",
        reminded: "bg-amber-600/30 text-amber-300 border border-amber-600/50",
        posted: "bg-green-600/30 text-green-300 border border-green-600/50",
        cancelled: "bg-red-700/30 text-red-300 border border-red-700/50",
    };

    const PLATFORM_OPTS = [
        { v: "instagram", t: "Instagram", url: "https://www.instagram.com/" },
        { v: "tiktok", t: "TikTok", url: "https://www.tiktok.com/upload" },
        { v: "facebook", t: "Facebook", url: "https://www.facebook.com/" },
        { v: "x", t: "X / Twitter", url: "https://x.com/compose/post" },
        { v: "other", t: "Other", url: null },
    ];
    const platformLink = (v) => PLATFORM_OPTS.find((p) => p.v === v)?.url;

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display font-black uppercase tracking-tight text-white text-xl flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-red-500" /> Content Queue
                    </h3>
                    <p className="mt-1 text-sm text-zinc-400 max-w-2xl">
                        Schedule a caption + image for any platform. When the time hits, you get an email reminder
                        with the caption ready to copy + a 1-tap link to open Instagram, TikTok, Facebook, or X.
                    </p>
                </div>
                <button
                    onClick={startCreate}
                    data-testid="content-queue-new"
                    className="bg-red-600 hover:bg-red-500 text-white font-display font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm flex items-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" /> New scheduled post
                </button>
            </div>

            {showForm && (
                <form
                    onSubmit={submit}
                    data-testid="content-queue-form"
                    className="mt-6 rounded-sm border border-white/10 bg-zinc-900 p-5 sm:p-6 space-y-5"
                >
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-zinc-400">Platforms</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {PLATFORM_OPTS.map((opt) => {
                                const on = form.platforms.includes(opt.v);
                                return (
                                    <button
                                        type="button"
                                        key={opt.v}
                                        onClick={() => togglePlatform(opt.v)}
                                        data-testid={`content-queue-platform-${opt.v}`}
                                        className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border ${
                                            on
                                                ? "border-red-600 bg-red-600/10 text-white"
                                                : "border-white/10 text-zinc-400 hover:text-white"
                                        }`}
                                    >
                                        {opt.t}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="cq-summary" className="text-xs uppercase tracking-widest text-zinc-400">
                            Job summary <span className="text-zinc-600">(for AI caption ideas — optional)</span>
                        </Label>
                        <div className="mt-2 flex flex-col sm:flex-row gap-2">
                            <Input
                                id="cq-summary"
                                data-testid="content-queue-job-summary"
                                value={jobSummary}
                                onChange={(ev) => setJobSummary(ev.target.value)}
                                placeholder="Replaced sway-bar end links on a BMW M3"
                                className="flex-1 bg-zinc-950 border-white/10 text-white"
                            />
                            <button
                                type="button"
                                onClick={suggestCaptions}
                                disabled={suggestBusy}
                                data-testid="content-queue-suggest-btn"
                                className="inline-flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-sm text-xs font-bold uppercase tracking-wider border border-white/10"
                            >
                                {suggestBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-red-500" />}
                                {suggestBusy ? "Writing…" : "Suggest captions"}
                            </button>
                        </div>
                        {suggestions.length > 0 && (
                            <div className="mt-3 space-y-2" data-testid="content-queue-suggestions">
                                {suggestions.map((s, i) => (
                                    <button
                                        type="button"
                                        key={i}
                                        onClick={() => applySuggestion(s)}
                                        data-testid={`content-queue-suggestion-${i}`}
                                        className="w-full text-left rounded-sm border border-white/10 hover:border-red-500/60 bg-zinc-950 hover:bg-red-950/10 p-3 transition-colors"
                                    >
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1">
                                            Option {i + 1} — tap to use
                                        </div>
                                        <div className="text-sm text-zinc-200 whitespace-pre-wrap">{s}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="cq-caption" className="text-xs uppercase tracking-widest text-zinc-400">
                            Caption <span className="text-zinc-600">({form.caption.length}/2200)</span>
                        </Label>
                        <textarea
                            id="cq-caption"
                            data-testid="content-queue-caption"
                            value={form.caption}
                            onChange={(ev) => setForm({ ...form, caption: ev.target.value })}
                            rows={5}
                            maxLength={2200}
                            placeholder={"Just finished a sway-bar end link refresh on a clean BMW M3. Old links were torn metal-to-metal — now silent and tight.\n\n#mobilemechanic #virginiabeach #bmw"}
                            className="mt-2 w-full bg-zinc-950 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600"
                        />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="cq-when" className="text-xs uppercase tracking-widest text-zinc-400">
                                When to remind you
                            </Label>
                            <Input
                                id="cq-when"
                                data-testid="content-queue-scheduled-at"
                                type="datetime-local"
                                value={form.scheduled_at}
                                onChange={(ev) => setForm({ ...form, scheduled_at: ev.target.value })}
                                className="mt-2 bg-zinc-950 border-white/10 text-white"
                            />
                        </div>
                        <div>
                            <Label htmlFor="cq-img" className="text-xs uppercase tracking-widest text-zinc-400">
                                Image URL <span className="text-zinc-600">(optional, paste from Photo Studio)</span>
                            </Label>
                            <Input
                                id="cq-img"
                                data-testid="content-queue-image-url"
                                value={form.image_url}
                                onChange={(ev) => setForm({ ...form, image_url: ev.target.value })}
                                placeholder="/api/static/gallery/sway-bar-end-link-diag.png"
                                className="mt-2 bg-zinc-950 border-white/10 text-white"
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="cq-notes" className="text-xs uppercase tracking-widest text-zinc-400">
                            Private notes <span className="text-zinc-600">(optional)</span>
                        </Label>
                        <Input
                            id="cq-notes"
                            data-testid="content-queue-notes"
                            value={form.notes}
                            onChange={(ev) => setForm({ ...form, notes: ev.target.value })}
                            placeholder="Tag the customer when posting"
                            className="mt-2 bg-zinc-950 border-white/10 text-white"
                        />
                    </div>

                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="content-queue-submit"
                            className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm text-sm"
                        >
                            {submitting ? "Saving…" : editing ? "Save changes" : "Add to queue"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowForm(false)}
                            className="border border-white/15 text-zinc-300 hover:text-white px-5 py-2.5 rounded-sm font-display font-bold uppercase tracking-wider text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className="mt-6 space-y-3">
                {loading && (
                    <div className="rounded-sm border border-white/10 bg-zinc-900 p-6 text-center text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin inline" />
                    </div>
                )}
                {!loading && posts.length === 0 && (
                    <div className="rounded-sm border border-dashed border-white/10 bg-zinc-950 p-8 text-center text-zinc-500 text-sm">
                        Nothing queued yet — click "New scheduled post" to plan your next drop.
                    </div>
                )}
                {!loading && posts.map((p) => {
                    const when = new Date(p.scheduled_at + (p.scheduled_at.endsWith("Z") ? "" : "Z"));
                    const overdue = p.status === "queued" && when.getTime() < Date.now();
                    const fullImg = p.image_url
                        ? (p.image_url.startsWith("http")
                            ? p.image_url
                            : `${API.replace(/\/api$/, "")}${p.image_url}`)
                        : "";
                    return (
                        <div
                            key={p.id}
                            data-testid={`content-queue-row-${p.id}`}
                            className={`rounded-sm border bg-zinc-900 p-4 sm:p-5 grid sm:grid-cols-[120px_1fr_auto] gap-4 items-start ${overdue ? "border-amber-600/40" : "border-white/10"}`}
                        >
                            {fullImg ? (
                                <img
                                    src={fullImg}
                                    alt=""
                                    className="w-full sm:w-[120px] aspect-square object-cover rounded-sm border border-white/10"
                                />
                            ) : (
                                <div className="w-full sm:w-[120px] aspect-square rounded-sm border border-dashed border-white/10 bg-zinc-950 flex items-center justify-center text-zinc-700">
                                    <ImageIcon className="w-6 h-6" />
                                </div>
                            )}
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLE[p.status] || STATUS_STYLE.queued}`}>
                                        {p.status}
                                    </span>
                                    {overdue && (
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400">
                                            Reminder due
                                        </span>
                                    )}
                                    <span className="text-xs text-zinc-400">
                                        {when.toLocaleString()}
                                    </span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {(p.platforms || []).map((v) => (
                                        <span key={v} className="text-[10px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded-sm">
                                            {v}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-2 text-sm text-zinc-300 whitespace-pre-wrap line-clamp-4">
                                    {p.caption}
                                </p>
                                {p.notes && (
                                    <p className="mt-1 text-xs text-zinc-500 italic">Note: {p.notes}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1.5 sm:items-end">
                                <button
                                    type="button"
                                    onClick={() => copyCaption(p.caption)}
                                    data-testid={`content-queue-copy-${p.id}`}
                                    className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white text-zinc-200 px-3 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                                >
                                    <Copy className="w-3.5 h-3.5" /> Copy caption
                                </button>
                                <div className="flex flex-wrap gap-1.5 justify-end">
                                    {(p.platforms || []).map((v) => {
                                        const url = platformLink(v);
                                        if (!url) return null;
                                        return (
                                            <a
                                                key={v}
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 border border-white/15 hover:border-red-500/60 hover:text-red-400 text-zinc-300 px-2.5 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                                            >
                                                <ExternalLink className="w-3 h-3" /> {v}
                                            </a>
                                        );
                                    })}
                                </div>
                                {p.status !== "posted" && (
                                    <button
                                        type="button"
                                        onClick={() => markPosted(p.id)}
                                        data-testid={`content-queue-posted-${p.id}`}
                                        className="inline-flex items-center gap-1.5 border border-green-700/50 text-green-400 hover:bg-green-700/10 px-3 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Mark posted
                                    </button>
                                )}
                                <div className="flex gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => startEdit(p)}
                                        className="text-[11px] text-zinc-400 hover:text-white underline"
                                    >
                                        Edit
                                    </button>
                                    <span className="text-zinc-700">·</span>
                                    <button
                                        type="button"
                                        onClick={() => cancelPost(p.id)}
                                        data-testid={`content-queue-delete-${p.id}`}
                                        className="text-[11px] text-zinc-400 hover:text-red-400 underline inline-flex items-center gap-1"
                                    >
                                        <Trash2 className="w-3 h-3" /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ReelStudio() {
    // files: [{file, previewUrl, caption: {text, family, size, position}}]
    const [files, setFiles] = useState([]);
    const [addingPhotos, setAddingPhotos] = useState(false);
    const addingRef = useRef(false);
    const [perPhoto, setPerPhoto] = useState(2.5);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [reels, setReels] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [directorPrompt, setDirectorPrompt] = useState("");
    const [directorBusy, setDirectorBusy] = useState(false);
    const [directorMsg, setDirectorMsg] = useState("");
    const [postSummary, setPostSummary] = useState("");
    const [postSuggestBusy, setPostSuggestBusy] = useState(false);
    const [postSuggestions, setPostSuggestions] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    // Live progress from the backend (`{phase: "rendering photo 3/5", current, total}`).
    // Populated by the polling loop in `generate()`.
    const [renderProgress, setRenderProgress] = useState(null);

    const FAMILY_OPTS = [
        { v: "sans", t: "Sans" },
        { v: "condensed", t: "Condensed" },
        { v: "serif", t: "Serif" },
        { v: "mono", t: "Mono" },
    ];
    const SIZE_OPTS = [
        { v: "S", t: "S" },
        { v: "M", t: "M" },
        { v: "L", t: "L" },
    ];
    const POS_OPTS = [
        { v: "top-left", t: "Top L" },
        { v: "top-center", t: "Top C" },
        { v: "bottom-left", t: "Btm L" },
        { v: "bottom-center", t: "Btm C" },
    ];
    const MOTION_OPTS = [
        { v: "zoom-in", t: "Zoom in" },
        { v: "zoom-out", t: "Zoom out" },
        { v: "pan-left", t: "Pan ←" },
        { v: "pan-right", t: "Pan →" },
        { v: "pan-up", t: "Pan ↑" },
        { v: "pan-down", t: "Pan ↓" },
        { v: "static", t: "Static" },
    ];
    const TRANSITION_OPTS = [
        { v: "fade", t: "Fade" },
        { v: "fade-black", t: "Fade Blk" },
        { v: "slide-left", t: "Slide ←" },
        { v: "slide-right", t: "Slide →" },
        { v: "slide-up", t: "Slide ↑" },
        { v: "slide-down", t: "Slide ↓" },
        { v: "wipe-left", t: "Wipe ←" },
        { v: "wipe-right", t: "Wipe →" },
        { v: "dissolve", t: "Dissolve" },
        { v: "circle-open", t: "Circle" },
        { v: "pixelize", t: "Pixel" },
        { v: "cut", t: "Cut" },
    ];

    const cycleField = (idx, field, options, dir = 1) => {
        const cur = files[idx]?.[field];
        const i = Math.max(0, options.findIndex((o) => o.v === cur));
        const next = options[(i + dir + options.length) % options.length].v;
        const f = [...files];
        f[idx] = { ...f[idx], [field]: next };
        setFiles(f);
    };

    const refreshList = async () => {
        setLoadingList(true);
        try {
            const r = await adminClient().get("/admin/reels");
            setReels(r.data.reels || []);
        } catch {
            // silent
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => { refreshList(); }, []);

    const onPick = async (incoming) => {
        if (busy || directorBusy || perPhotoDirectorBusy || addingRef.current || !incoming.length) return;
        addingRef.current = true;
        setAddingPhotos(true);
        try {
        const next = [...files];
        const added = [];
        const hasHeic = incoming.some((f) =>
            /^image\/(heic|heif)/.test(f.type) || /\.hei[cf]$/i.test(f.name),
        );
        if (hasHeic) toast.message("Converting HEIC photo(s)…", { duration: 4000 });

        // Shrink huge photos client-side so 7 lossless PNGs don't blow past the
        // ingress request limit. 3MB soft cap, long-edge cap 1920 (matches the
        // 1080×1920 output — larger images add zero visible quality).
        const _shrinkIfLarge = async (file) => {
            const SOFT_LIMIT_BYTES = 3 * 1024 * 1024;
            if (file.size <= SOFT_LIMIT_BYTES) return file;
            try {
                const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
                const maxEdge = 1920;
                let { width, height } = bitmap;
                const longEdge = Math.max(width, height);
                if (longEdge > maxEdge) {
                    const scale = maxEdge / longEdge;
                    width = Math.round(width * scale);
                    height = Math.round(height * scale);
                }
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
                bitmap.close && bitmap.close();
                const blob = await new Promise((res) =>
                    canvas.toBlob(res, "image/jpeg", 0.88),
                );
                if (!blob) return file;
                console.info(
                    `[Reel] shrunk ${file.name}: ${(file.size / 1e6).toFixed(1)}MB → ${(blob.size / 1e6).toFixed(1)}MB`,
                );
                return new File(
                    [blob], file.name.replace(/\.[^.]+$/, "") + ".jpg",
                    { type: "image/jpeg" },
                );
            } catch (err) {
                console.warn("client shrink failed (using original):", err);
                return file;
            }
        };

        for (const f of incoming) {
            if (next.length + added.length >= 10) { toast.error("Max 10 photos total."); break; }
            if (!f.size) { toast.error(`${f.name}: file is empty`); continue; }
            const isHeic =
                /^image\/(heic|heif)/.test(f.type) || /\.hei[cf]$/i.test(f.name);
            const isOther = /^image\/(png|jpe?g|webp)$/.test(f.type) || (!f.type && /\.(png|jpe?g|webp)$/i.test(f.name));
            if (!isHeic && !isOther) {
                toast.error(`${f.name}: only PNG/JPG/WebP/HEIC`);
                continue;
            }
            let workingFile = f;
            if (isHeic) {
                try {
                    const heic2any = (await import("heic2any")).default;
                    const blob = await heic2any({ blob: f, toType: "image/jpeg", quality: 0.9 });
                    const jpgBlob = Array.isArray(blob) ? blob[0] : blob;
                    workingFile = new File(
                        [jpgBlob],
                        f.name.replace(/\.hei[cf]$/i, ".jpg"),
                        { type: "image/jpeg" },
                    );
                } catch (err) {
                    console.error("HEIC conversion failed:", err);
                    toast.error(`${f.name}: couldn't convert HEIC — try saving as JPG`);
                    continue;
                }
            }
            // Downsize before we push. Runs after HEIC conversion so we shrink
            // the JPEG output rather than the massive HEIC container.
            workingFile = await _shrinkIfLarge(workingFile);
            if (workingFile.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8MB after photo optimization`); continue; }
            // default fit: cover. We'll flip to "contain" below if the source
            // image turns out to be landscape (wider than 9:16), so desktop
            // screenshots aren't cropped to a useless center slice.
            const entry = {
                file: workingFile,
                previewUrl: URL.createObjectURL(workingFile),
                // sizePx is the source of truth for caption size. null = use the
                // S/M/L preset; a positive int = exact pixel size at REEL_W=1080
                // (matches both the preview and the exported MP4 1:1).
                caption: { text: "", family: "sans", size: "M", position: "top-left", sizePx: null },
                motion: "zoom-in",
                transition: "fade",
                fit: "cover",
            };
            added.push(entry);
        }
        // Detect orientation per file (async via Image load), then update fits in
        // one batch so React only re-renders once.
        if (added.length === 0) return;
        await Promise.all(
            added.map(
                (a) =>
                    new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => {
                            // 9:16 frame aspect = 0.5625. If the source is wider
                            // than ~0.75 (i.e. closer to square or landscape),
                            // a cover-crop would lose most of the image — use contain.
                            const ratio = img.naturalWidth / img.naturalHeight;
                            if (ratio > 0.75) a.fit = "contain";
                            resolve();
                        };
                        img.onerror = () => resolve();
                        img.src = a.previewUrl;
                    }),
            ),
        );
        setFiles([...next, ...added]);
        } finally {
            addingRef.current = false;
            setAddingPhotos(false);
        }
    };

    const removePhoto = (idx) => {
        const next = [...files];
        const [removed] = next.splice(idx, 1);
        if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
        setFiles(next);
    };

    const movePhoto = (idx, dir) => {
        const next = [...files];
        const tgt = idx + dir;
        if (tgt < 0 || tgt >= next.length) return;
        [next[idx], next[tgt]] = [next[tgt], next[idx]];
        setFiles(next);
    };

    const updateCaption = (idx, patch) => {
        const next = [...files];
        next[idx] = { ...next[idx], caption: { ...next[idx].caption, ...patch } };
        setFiles(next);
    };

    const [perPhotoDirectorIdx, setPerPhotoDirectorIdx] = useState(null);
    const [perPhotoDirectorPrompt, setPerPhotoDirectorPrompt] = useState("");
    const [perPhotoDirectorBusy, setPerPhotoDirectorBusy] = useState(false);

    const callDirector = async (prompt, targetIndex) => {
        if (files.length < 1) {
            toast.error("Add some photos first.");
            return false;
        }
        try {
            const res = await adminClient().post("/admin/reel/director", {
                prompt,
                per_photo: perPhoto,
                photos: files.map((f) => ({
                    ...f.caption,
                    motion: f.motion || "zoom-in",
                    transition: f.transition || "fade",
                    fit: f.fit || "cover",
                })),
                target_index: typeof targetIndex === "number" ? targetIndex : null,
                allow_reorder: typeof targetIndex !== "number",
            });
            const updated = res.data.photos || [];
            if (updated.length !== files.length) {
                toast.error("Director output mismatched photo count.");
                return false;
            }
            let next = files.map((f, i) => ({
                ...f,
                caption: {
                    text: updated[i].text,
                    family: updated[i].family,
                    size: updated[i].size,
                    position: updated[i].position,
                },
                motion: updated[i].motion,
                transition: updated[i].transition,
                fit: updated[i].fit || f.fit || "cover",
            }));
            // Apply reorder if AI returned one
            const order = res.data.order;
            if (Array.isArray(order) && order.length === next.length) {
                next = order.map((i) => next[i]);
            }
            setFiles(next);
            setPerPhoto(res.data.per_photo || perPhoto);
            setDirectorMsg(res.data.message || "Updated.");
            toast.success("Director applied the edits.");
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Director failed.");
            return false;
        }
    };

    const runDirector = async () => {
        const prompt = directorPrompt.trim();
        if (prompt.length < 3) return toast.error("Tell the director what to change.");
        setDirectorBusy(true);
        setDirectorMsg("");
        await callDirector(prompt, null);
        setDirectorBusy(false);
    };

    const runPerPhotoDirector = async (idx) => {
        const prompt = perPhotoDirectorPrompt.trim();
        if (prompt.length < 3) return toast.error("Describe what to change.");
        setPerPhotoDirectorBusy(true);
        const ok = await callDirector(prompt, idx);
        setPerPhotoDirectorBusy(false);
        if (ok) {
            setPerPhotoDirectorIdx(null);
            setPerPhotoDirectorPrompt("");
        }
    };

    const suggestPostCaptions = async () => {
        const summary = postSummary.trim();
        if (summary.length < 4) return toast.error("Tell me what this reel is about first.");
        const plats = ["instagram"];
        setPostSuggestBusy(true);
        setPostSuggestions([]);
        try {
            const res = await adminClient().post("/admin/suggest-captions", {
                job_summary: summary,
                platforms: plats,
            });
            setPostSuggestions(res.data.options || []);
            if (!(res.data.options || []).length) toast.error("No options returned.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Caption suggest failed.");
        } finally {
            setPostSuggestBusy(false);
        }
    };

    const copyPostCaption = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Caption copied — paste it on IG/TikTok/FB.");
        } catch {
            toast.error("Copy failed.");
        }
    };

    const generate = async () => {
        // Guard against double-clicks even with the disabled-attr fallback.
        if (busy) return;
        if (files.length < 2) return toast.error("Pick at least 2 photos.");
        if (files.length > 10) return toast.error("Max 10 photos.");
        const fd = new FormData();
        fd.append(
            "captions_json",
            JSON.stringify(files.map((f) => ({
                ...f.caption,
                // Backend reads `sizePx` (camelCase). null means "use S/M/L preset".
                sizePx: f.caption?.sizePx || null,
                motion: f.motion || "zoom-in",
                transition: f.transition || "fade",
                fit: f.fit || "cover",
            }))),
        );
        fd.append("per_photo", String(perPhoto));
        files.forEach((f) => fd.append("photos", f.file));

        setBusy(true);
        setResult(null);
        setRenderProgress({ phase: "Uploading photos…", current: 0, total: files.length });

        // Step 1: queue the render. This call should be FAST (just uploads photos +
        // returns a job id). Long upload+render synchronously was triggering the
        // cloud ingress's "Network error" on big reels.
        let jobId;
        try {
            const queued = await adminClient().post("/admin/reels", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                // 120s is plenty for the upload portion alone, even with 10 photos.
                timeout: 120000,
            });
            jobId = queued.data?.id;
            if (!jobId) throw new Error("No job id returned.");
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Upload failed.";
            toast.error(`Render failed: ${detail}`, { duration: 15000 });
            console.error("[Reel upload failed]", e);
            setBusy(false);
            setRenderProgress(null);
            return;
        }

        setRenderProgress({ phase: "Preparing…", current: 0, total: files.length });

        // Step 2: poll for completion. Even if the cloud ingress times out a single
        // poll, the next one picks up the state. Total wait cap = 20 min — the
        // backend auto-resumes across restarts, so we give it plenty of runway.
        const started = Date.now();
        const MAX_WAIT_MS = 20 * 60 * 1000;
        let not_found_count = 0;
        try {
            // small initial delay so we don't slam the server before ffmpeg even starts
            await new Promise((r) => setTimeout(r, 1500));
            while (Date.now() - started < MAX_WAIT_MS) {
                try {
                    const sres = await adminClient().get(`/admin/reels/${jobId}/status`, { timeout: 15000 });
                    const data = sres.data || {};
                    not_found_count = 0;
                    if (data.status === "done") {
                        setResult(data);
                        toast.success("Reel rendered.");
                        refreshList();
                        setBusy(false);
                        setRenderProgress(null);
                        return;
                    }
                    if (data.status === "failed") {
                        const detail = data.error || "Render failed.";
                        toast.error(`Render failed: ${detail}`, { duration: 15000 });
                        console.error("[Reel render failed]", data);
                        setBusy(false);
                        setRenderProgress(null);
                        return;
                    }
                    // Still rendering — surface backend progress in the UI.
                    if (data.phase) {
                        setRenderProgress({
                            phase: data.phase,
                            current: data.progress_current ?? 0,
                            total: data.progress_total ?? files.length,
                        });
                    }
                } catch (e) {
                    // Transient poll errors are OK — keep polling. Only quit on
                    // repeated 404s. Bumped from 5 → 20 (~60s of "not found")
                    // to give the backend startup-resume hook time to catch up
                    // after a hot-reload or pod restart.
                    if (e?.response?.status === 404) {
                        not_found_count += 1;
                        if (not_found_count >= 20) {
                            toast.error("Render failed: job lost. Try again.", { duration: 15000 });
                            setBusy(false);
                            setRenderProgress(null);
                            return;
                        }
                    }
                }
                await new Promise((r) => setTimeout(r, 3000));
            }
            toast.error("Render is taking too long (>20 min). It may still finish — refresh the saved reels list in a minute.", { duration: 15000 });
        } finally {
            setBusy(false);
            setRenderProgress(null);
        }
    };

    const deleteReel = async (id) => {
        if (!window.confirm("Delete this reel?")) return;
        try {
            await adminClient().delete(`/admin/reels/${id}`);
            toast.success("Deleted.");
            if (result?.id === id) setResult(null);
            refreshList();
        } catch {
            toast.error("Couldn't delete.");
        }
    };

    const fullUrl = (path) => `${API.replace(/\/api$/, "")}${path}`;
    const totalDuration = (files.length * perPhoto).toFixed(1);

    const resetEdits = () => {
        if (files.length > 0 || directorPrompt || directorMsg || postSummary || postSuggestions.length) {
            if (!window.confirm("Clear all uploaded photos, captions, motion, transitions, and AI Director state? This won't delete already-rendered reels.")) {
                return;
            }
        }
        // Revoke any blob URLs we created for previews so we don't leak memory.
        files.forEach((f) => {
            if (f.previewUrl && f.previewUrl.startsWith("blob:")) {
                try { URL.revokeObjectURL(f.previewUrl); } catch { /* noop */ }
            }
        });
        setFiles([]);
        setPerPhoto(2.5);
        setResult(null);
        setDirectorPrompt("");
        setDirectorMsg("");
        setPerPhotoDirectorIdx(null);
        setPerPhotoDirectorPrompt("");
        setPostSummary("");
        setPostSuggestions([]);
        toast.success("Reel editor cleared.");
    };

    return (
        <div className="mt-6 grid min-w-0 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
            <fieldset disabled={busy || addingPhotos || directorBusy || perPhotoDirectorBusy}
                className="min-w-0 rounded-sm border border-white/10 bg-zinc-900 p-5 sm:p-6 space-y-5"
                data-testid="reel-studio-form"
            >
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="font-display font-black uppercase tracking-tight text-white text-xl flex items-center gap-2">
                            <Film className="w-5 h-5 text-red-500" /> Reel Studio
                        </h3>
                        <p className="mt-1 text-sm text-zinc-400">
                            Upload 2-10 photos. Each photo gets its own optional caption sticker — pick the
                            font, size, and position. Render = 1080×1920 reel ready for IG/TikTok.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={resetEdits}
                        disabled={busy || addingPhotos || directorBusy || perPhotoDirectorBusy}
                        data-testid="reel-studio-reset"
                        className="shrink-0 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-red-400 border border-white/10 hover:border-red-500/50 px-2.5 py-1.5 rounded-sm font-bold"
                        title="Clear all photos, captions, motion, and AI Director state"
                    >
                        <RotateCcw className="w-3 h-3" /> Reset edits
                    </button>
                </div>

                <div>
                    <Label data-testid="reel-studio-photo-count" className="text-xs uppercase tracking-widest text-zinc-400">
                        Photos <span className="text-zinc-600">({files.length}/10 · ~{totalDuration}s)</span>
                    </Label>
                    <div className="mt-2">
                        <MediaDropzone prefix="reel-studio" title="Drop photos here, or browse"
                            hint="2–10 photos · PNG/JPG/WebP/HEIC · 8MB after optimization"
                            accept="image/png,image/jpeg,image/webp,image/heic,image/heif,.heic,.heif"
                            disabled={busy || addingPhotos || directorBusy || perPhotoDirectorBusy || files.length >= 10}
                            preparing={addingPhotos} onFiles={onPick} />
                    </div>
                </div>

                {files.length > 0 && (
                    <div className="space-y-4" data-testid="reel-studio-rows">
                        {files.map((f, i) => (
                            <div
                                key={i}
                                className="min-w-0 rounded-sm border border-white/10 bg-zinc-950 p-3 grid grid-cols-[64px_minmax(0,1fr)] sm:grid-cols-[88px_minmax(0,1fr)] gap-3"
                                data-testid={`reel-studio-row-${i}`}
                            >
                                <div className="relative">
                                    <img
                                        src={f.previewUrl}
                                        alt=""
                                        className="w-full aspect-[9/16] object-cover rounded-sm border border-white/10"
                                    />
                                    <span className="absolute top-1 left-1 text-[10px] font-bold text-white bg-black/70 px-1.5 py-0.5 rounded-sm">
                                        #{i + 1}
                                    </span>
                                </div>
                                <div className="min-w-0 space-y-2">
                                    <textarea
                                        value={f.caption.text}
                                        onChange={(ev) => updateCaption(i, { text: ev.target.value.slice(0, 280) })}
                                        rows={2}
                                        placeholder="Type your own on-screen caption for this photo (you control this — overrides AI suggestions)"
                                        data-testid={`reel-studio-caption-${i}`}
                                        className="w-full bg-zinc-900 border border-white/10 rounded-sm px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600 resize-none"
                                    />
                                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-center">
                                        <div className="flex flex-wrap items-center gap-1">
                                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Font</span>
                                            {FAMILY_OPTS.map((o) => (
                                                <button
                                                    type="button"
                                                    key={o.v}
                                                    onClick={() => updateCaption(i, { family: o.v })}
                                                    data-testid={`reel-studio-font-${i}-${o.v}`}
                                                    className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${
                                                        f.caption.family === o.v
                                                            ? "bg-red-600/20 border-red-600 text-white"
                                                            : "border-white/10 text-zinc-400 hover:text-white"
                                                    }`}
                                                >{o.t}</button>
                                            ))}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1">
                                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Size</span>
                                            {SIZE_OPTS.map((o) => (
                                                <button
                                                    type="button"
                                                    key={o.v}
                                                    onClick={() => updateCaption(i, { size: o.v, sizePx: null })}
                                                    data-testid={`reel-studio-size-${i}-${o.v}`}
                                                    className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${
                                                        !f.caption.sizePx && f.caption.size === o.v
                                                            ? "bg-red-600/20 border-red-600 text-white"
                                                            : "border-white/10 text-zinc-400 hover:text-white"
                                                    }`}
                                                >{o.t}</button>
                                            ))}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1">
                                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Pos</span>
                                            {POS_OPTS.map((o) => (
                                                <button
                                                    type="button"
                                                    key={o.v}
                                                    onClick={() => updateCaption(i, { position: o.v })}
                                                    data-testid={`reel-studio-position-${i}-${o.v}`}
                                                    className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${
                                                        f.caption.position === o.v
                                                            ? "bg-red-600/20 border-red-600 text-white"
                                                            : "border-white/10 text-zinc-400 hover:text-white"
                                                    }`}
                                                >{o.t}</button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Per-photo caption size slider — exact pixel size at REEL_W=1080.
                                        Affects BOTH the preview AND the exported MP4 1:1 so what you
                                        see in preview is what shows up in the final reel. */}
                                    {(() => {
                                        const PRESET_PX = { S: 100, M: 152, L: 216 };
                                        const activePx = f.caption.sizePx || PRESET_PX[f.caption.size] || 152;
                                        const pctOfWidth = ((activePx / 1080) * 100).toFixed(1);
                                        return (
                                            <div className="flex min-w-0 flex-wrap items-center gap-2 pt-0.5">
                                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
                                                    Caption size
                                                </span>
                                                <input
                                                    type="range"
                                                    min={60}
                                                    max={300}
                                                    step={2}
                                                    value={activePx}
                                                    onChange={(ev) => updateCaption(i, { sizePx: Number(ev.target.value) })}
                                                    data-testid={`reel-studio-caption-size-${i}`}
                                                    className="min-w-0 w-full basis-full sm:basis-0 sm:flex-1 accent-red-600"
                                                    title="Drag to fine-tune caption size for this photo"
                                                />
                                                <span data-testid={`reel-studio-caption-size-value-${i}`} className="text-[10px] text-zinc-400 tabular-nums sm:w-14 sm:text-right">
                                                    {activePx}px · {pctOfWidth}%
                                                </span>
                                                {f.caption.sizePx ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => updateCaption(i, { sizePx: null })}
                                                        className="text-[10px] text-zinc-400 hover:text-white underline"
                                                        title="Go back to the S/M/L preset"
                                                        data-testid={`reel-studio-caption-size-reset-${i}`}
                                                    >reset</button>
                                                ) : null}
                                            </div>
                                        );
                                    })()}
                                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-center pt-1">
                                        <label className="inline-flex max-w-full flex-wrap items-center gap-1.5">
                                            <span className="text-[10px] uppercase tracking-widest text-cyan-400/80">Motion</span>
                                            <select
                                                value={f.motion || "zoom-in"}
                                                onChange={(ev) => {
                                                    const next = [...files];
                                                    next[i] = { ...next[i], motion: ev.target.value };
                                                    setFiles(next);
                                                }}
                                                data-testid={`reel-studio-motion-${i}`}
                                                className="max-w-full bg-zinc-900 border border-cyan-700/40 text-cyan-200 text-[11px] rounded-sm px-2 py-1 focus:outline-none focus:border-cyan-500"
                                            >
                                                {MOTION_OPTS.map((o) => (
                                                    <option key={o.v} value={o.v} className="bg-zinc-900">{o.t}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="inline-flex max-w-full flex-wrap items-center gap-1.5">
                                            <span className="text-[10px] uppercase tracking-widest text-amber-400/80">Fit</span>
                                            <select
                                                value={f.fit || "cover"}
                                                onChange={(ev) => {
                                                    const next = [...files];
                                                    next[i] = { ...next[i], fit: ev.target.value };
                                                    setFiles(next);
                                                }}
                                                data-testid={`reel-studio-fit-${i}`}
                                                title="Cover = crop to fill frame. Contain = show whole image with black bars (best for screenshots)."
                                                className="max-w-full bg-zinc-900 border border-amber-700/40 text-amber-200 text-[11px] rounded-sm px-2 py-1 focus:outline-none focus:border-amber-500"
                                            >
                                                <option value="cover" className="bg-zinc-900">Cover (crop)</option>
                                                <option value="contain" className="bg-zinc-900">Contain (whole)</option>
                                            </select>
                                        </label>
                                        {i > 0 && (
                                            <label className="inline-flex max-w-full flex-wrap items-center gap-1.5">
                                                <span className="text-[10px] uppercase tracking-widest text-purple-400/80">Entry</span>
                                                <select
                                                    value={f.transition || "fade"}
                                                    onChange={(ev) => {
                                                        const next = [...files];
                                                        next[i] = { ...next[i], transition: ev.target.value };
                                                        setFiles(next);
                                                    }}
                                                    data-testid={`reel-studio-transition-${i}`}
                                                    className="max-w-full bg-zinc-900 border border-purple-700/40 text-purple-200 text-[11px] rounded-sm px-2 py-1 focus:outline-none focus:border-purple-500"
                                                >
                                                    {TRANSITION_OPTS.map((o) => (
                                                        <option key={o.v} value={o.v} className="bg-zinc-900">{o.t}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPerPhotoDirectorIdx(i);
                                                setPerPhotoDirectorPrompt("");
                                            }}
                                            data-testid={`reel-studio-photo-director-${i}`}
                                            className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border border-red-700/40 text-red-300 hover:bg-red-900/20 inline-flex items-center gap-1"
                                            title="Ask AI to edit only this photo"
                                        >
                                            <Sparkles className="w-2.5 h-2.5" /> Edit just this
                                        </button>
                                    </div>
                                    {perPhotoDirectorIdx === i && (
                                        <div className="mt-2 rounded-sm border border-red-700/40 bg-red-950/20 p-2.5 space-y-2" data-testid={`reel-studio-photo-director-prompt-${i}`}>
                                            <Input
                                                value={perPhotoDirectorPrompt}
                                                onChange={(ev) => setPerPhotoDirectorPrompt(ev.target.value.slice(0, 400))}
                                                placeholder="Change only this photo — e.g. 'pan up, smaller caption, slide-up transition'"
                                                className="bg-zinc-950 border-white/10 text-white text-xs"
                                                autoFocus
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => runPerPhotoDirector(i)}
                                                    disabled={perPhotoDirectorBusy}
                                                    className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white px-3 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                                                >
                                                    {perPhotoDirectorBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                                    Apply
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setPerPhotoDirectorIdx(null)}
                                                    className="text-[10px] text-zinc-400 hover:text-white"
                                                >Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex gap-2 text-[11px] text-zinc-400 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => movePhoto(i, -1)}
                                            disabled={i === 0}
                                            className="hover:text-white disabled:opacity-30"
                                        >↑ Move up</button>
                                        <span className="text-zinc-700">·</span>
                                        <button
                                            type="button"
                                            onClick={() => movePhoto(i, 1)}
                                            disabled={i === files.length - 1}
                                            className="hover:text-white disabled:opacity-30"
                                        >Move down ↓</button>
                                        <span className="text-zinc-700">·</span>
                                        <button
                                            type="button"
                                            onClick={() => removePhoto(i)}
                                            data-testid={`reel-studio-remove-${i}`}
                                            className="hover:text-red-400 inline-flex items-center gap-1"
                                        ><Trash2 className="w-3 h-3" /> Remove</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div>
                    <Label htmlFor="rs-per" className="text-xs uppercase tracking-widest text-zinc-400">
                        Seconds per photo <span className="text-zinc-600">({perPhoto.toFixed(1)}s)</span>
                    </Label>
                    <input
                        id="rs-per"
                        type="range"
                        min="1.5"
                        max="4"
                        step="0.1"
                        value={perPhoto}
                        onChange={(ev) => setPerPhoto(parseFloat(ev.target.value))}
                        data-testid="reel-studio-per-photo"
                        className="mt-2 w-full accent-red-600"
                    />
                </div>

                {files.length > 0 && (
                    <div className="rounded-sm border border-red-700/30 bg-red-950/10 p-4 space-y-3" data-testid="reel-director">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-red-500" />
                            <h4 className="font-display font-bold uppercase tracking-tight text-white text-sm">
                                AI Director
                            </h4>
                        </div>
                        <p className="text-[11px] text-zinc-400 leading-relaxed">
                            Type plain English — e.g.,
                            <em className="text-zinc-300"> "make all captions short and punchy, switch to condensed font, slow down photo 2"</em>{" "}
                            — and the director rewrites every photo's caption, font, size, position, and timing.
                        </p>
                        <textarea
                            value={directorPrompt}
                            onChange={(ev) => setDirectorPrompt(ev.target.value.slice(0, 800))}
                            rows={3}
                            placeholder="What do you want changed?"
                            data-testid="reel-director-prompt"
                            className="w-full bg-zinc-950 border border-white/10 rounded-sm px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600 resize-none"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={runDirector}
                                disabled={directorBusy}
                                data-testid="reel-director-run"
                                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                            >
                                {directorBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                {directorBusy ? "Thinking…" : "Apply edits"}
                            </button>
                            {directorMsg && (
                                <span className="text-[11px] text-green-400">✓ {directorMsg}</span>
                            )}
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    onClick={() => setPreviewOpen(true)}
                    disabled={files.length < 1}
                    data-testid="reel-studio-preview-btn"
                    className="w-full border border-white/20 hover:border-white text-white font-display font-bold uppercase tracking-wider px-5 py-3 rounded-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    title="Preview the slideshow in your browser before rendering the MP4"
                >
                    <Play className="w-4 h-4" />
                    Preview Reel
                </button>

                <button
                    type="button"
                    onClick={generate}
                    disabled={busy || files.length < 2}
                    data-testid="reel-studio-generate"
                    className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider px-5 py-3 rounded-sm flex items-center justify-center gap-2"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
                    {busy
                        ? (renderProgress?.phase
                            ? `${renderProgress.phase}${renderProgress.total ? ` (${renderProgress.current || 0}/${renderProgress.total})` : ""}`
                            : "Starting render…")
                        : "Generate reel"}
                </button>

                {previewOpen && (
                    <ReelPreviewModal
                        files={files}
                        perPhoto={perPhoto}
                        onClose={() => setPreviewOpen(false)}
                    />
                )}
                <p className="text-[11px] text-zinc-500">
                    Tip: most IG creators add background music in the IG editor itself — keep this video silent and add audio when posting.
                </p>
            </fieldset>

            <div className="min-w-0 space-y-6">
                {result && (
                    <div className="rounded-sm border border-green-700/40 bg-zinc-900 p-5">
                        <h4 className="font-display font-bold uppercase tracking-tight text-white text-base flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400" /> Latest render
                        </h4>
                        <video
                            src={fullUrl(result.url)}
                            controls
                            playsInline
                            data-testid="reel-studio-preview"
                            className="mt-4 w-full rounded-sm border border-white/10 bg-black"
                        />
                        <div className="mt-3 flex flex-wrap gap-2">
                            <a
                                href={fullUrl(result.url)}
                                download={`limit-break-reel-${result.id}.mp4`}
                                data-testid="reel-studio-download"
                                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                            >
                                Download MP4
                            </a>
                            <button
                                type="button"
                                onClick={() => deleteReel(result.id)}
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500/60 hover:text-red-400 text-zinc-300 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                                Step 2 — upload to your platform
                            </div>
                            <div className="grid grid-cols-3 gap-2" data-testid="reel-studio-platform-links">
                                <a
                                    href="https://www.instagram.com/"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="reel-studio-open-instagram"
                                    className="inline-flex flex-col items-center gap-1 border border-white/10 hover:border-pink-500/60 hover:text-pink-400 text-zinc-300 px-2 py-3 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    Instagram
                                </a>
                                <a
                                    href="https://www.tiktok.com/upload"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="reel-studio-open-tiktok"
                                    className="inline-flex flex-col items-center gap-1 border border-white/10 hover:border-cyan-500/60 hover:text-cyan-400 text-zinc-300 px-2 py-3 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    TikTok
                                </a>
                                <a
                                    href="https://www.facebook.com/reel/create"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="reel-studio-open-facebook"
                                    className="inline-flex flex-col items-center gap-1 border border-white/10 hover:border-blue-500/60 hover:text-blue-400 text-zinc-300 px-2 py-3 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    Facebook
                                </a>
                            </div>
                            <p className="mt-2 text-[10px] text-zinc-500 leading-relaxed">
                                After downloading, open one of the apps on your phone and upload the MP4. The video is already 9:16 — perfect for Reels, TikTok, and FB Reels.
                            </p>
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                                <Sparkles className="w-3 h-3 text-red-500" /> Step 3 — write the post caption
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Input
                                    value={postSummary}
                                    onChange={(ev) => setPostSummary(ev.target.value)}
                                    placeholder="What's this reel about? (one line)"
                                    data-testid="reel-studio-post-summary"
                                    className="flex-1 bg-zinc-950 border-white/10 text-white text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={suggestPostCaptions}
                                    disabled={postSuggestBusy}
                                    data-testid="reel-studio-post-suggest"
                                    className="inline-flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white px-3 py-2 rounded-sm text-[11px] font-bold uppercase tracking-wider border border-white/10"
                                >
                                    {postSuggestBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 text-red-500" />}
                                    {postSuggestBusy ? "Writing…" : "Suggest"}
                                </button>
                            </div>
                            {postSuggestions.length > 0 && (
                                <div className="mt-3 space-y-2" data-testid="reel-studio-post-suggestions">
                                    {postSuggestions.map((s, i) => (
                                        <button
                                            type="button"
                                            key={i}
                                            onClick={() => copyPostCaption(s)}
                                            className="w-full text-left rounded-sm border border-white/10 hover:border-red-500/60 bg-zinc-950 hover:bg-red-950/10 p-2.5 transition-colors"
                                        >
                                            <div className="text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1 flex items-center gap-1">
                                                <Copy className="w-2.5 h-2.5" /> Option {i + 1} — tap to copy
                                            </div>
                                            <div className="text-xs text-zinc-200 whitespace-pre-wrap">{s}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mt-3 text-[11px] text-zinc-500">
                            {result.duration}s · {(result.bytes / 1024 / 1024).toFixed(1)} MB · {result.photo_count} photos
                        </div>
                    </div>
                )}

                <div className="rounded-sm border border-white/10 bg-zinc-900 p-5">
                    <div className="flex items-center justify-between">
                        <h4 className="font-display font-bold uppercase tracking-tight text-white text-base">Saved reels</h4>
                        <span className="text-[11px] text-zinc-500">{reels.length} on file</span>
                    </div>
                    {loadingList && <div className="mt-3 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
                    {!loadingList && reels.length === 0 && <p className="mt-3 text-sm text-zinc-500">No reels yet — generate one.</p>}
                    {!loadingList && reels.length > 0 && (
                        <ul className="mt-3 space-y-2" data-testid="reel-studio-list">
                            {reels.map((r) => (
                                <li
                                    key={r.id}
                                    className="flex items-center gap-3 rounded-sm border border-white/10 bg-zinc-950 px-3 py-2"
                                >
                                    <Film className="w-4 h-4 text-zinc-500 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white font-mono truncate">{r.id}</div>
                                        <div className="text-[11px] text-zinc-500">{(r.bytes / 1024 / 1024).toFixed(1)} MB · {new Date(r.created_at).toLocaleString()}</div>
                                    </div>
                                    <a
                                        href={fullUrl(r.url)}
                                        download={`limit-break-reel-${r.id}.mp4`}
                                        className="text-[11px] text-zinc-300 hover:text-white underline"
                                    >Download</a>
                                    <button
                                        type="button"
                                        onClick={() => deleteReel(r.id)}
                                        className="text-zinc-500 hover:text-red-400"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function SeoStudio() {
    const [siteUrl, setSiteUrl] = useState("https://limitbreakperformance.com");
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pingResult, setPingResult] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await adminClient().get("/admin/seo/status");
                if (!cancelled) setStatus(r.data);
            } catch {
                // silent
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const ping = async () => {
        const url = siteUrl.trim().replace(/\/$/, "");
        if (!/^https?:\/\//.test(url)) {
            return toast.error("Site URL must start with http:// or https://");
        }
        setBusy(true);
        setPingResult(null);
        try {
            const r = await adminClient().post("/admin/seo/indexnow", { site_url: url });
            setPingResult(r.data);
            if (r.data.ok) {
                toast.success(`Pinged Bing/Yandex with ${r.data.url_count} URLs.`);
            } else {
                toast.error(`IndexNow returned ${r.data.status_code}.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ping failed.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied.`);
        } catch {
            toast.error("Copy failed — long-press to copy.");
        }
    };

    const sitemapUrl = `${siteUrl.replace(/\/$/, "")}/sitemap.xml`;
    const keyUrl = status?.indexnow_key_filename
        ? `${siteUrl.replace(/\/$/, "")}/${status.indexnow_key_filename}`
        : "";

    return (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="rounded-sm border border-white/10 bg-zinc-900 p-5 sm:p-6 space-y-5" data-testid="seo-tools">
                <div>
                    <h3 className="font-display font-black uppercase tracking-tight text-white text-xl flex items-center gap-2">
                        <Search className="w-5 h-5 text-red-500" /> SEO Tools
                    </h3>
                    <p className="mt-1 text-sm text-zinc-400">
                        Tell Bing, Yandex, and Google about new content the moment you push it.
                        IndexNow handles Bing + Yandex automatically with one click — Google
                        requires a manual setup (one-time, instructions on the right).
                    </p>
                </div>

                <div>
                    <Label htmlFor="seo-site" className="text-xs uppercase tracking-widest text-zinc-400">
                        Site URL
                    </Label>
                    <Input
                        id="seo-site"
                        data-testid="seo-site-url"
                        value={siteUrl}
                        onChange={(e) => setSiteUrl(e.target.value)}
                        className="mt-2 bg-zinc-950 border-white/10 text-white"
                    />
                </div>

                <div className="rounded-sm border border-white/10 bg-zinc-950 p-4 space-y-2 text-xs text-zinc-300">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-zinc-500 uppercase tracking-widest text-[10px]">Sitemap</span>
                        <button
                            type="button"
                            onClick={() => copy(sitemapUrl, "Sitemap URL")}
                            className="text-zinc-300 hover:text-white text-[11px] inline-flex items-center gap-1"
                        ><Copy className="w-3 h-3" /> Copy</button>
                    </div>
                    <code className="block break-all text-red-400">{sitemapUrl}</code>

                    <div className="flex items-center justify-between gap-2 pt-2">
                        <span className="text-zinc-500 uppercase tracking-widest text-[10px]">URLs in sitemap</span>
                        <span className="text-white font-bold">{status?.url_count ?? "—"}</span>
                    </div>

                    <div className="flex items-center justify-between gap-2 pt-2">
                        <span className="text-zinc-500 uppercase tracking-widest text-[10px]">IndexNow key</span>
                        <span className={status?.indexnow_key_present ? "text-green-400" : "text-amber-400"}>
                            {status?.indexnow_key_present ? "Configured ✓" : "Missing"}
                        </span>
                    </div>
                    {status?.indexnow_key_filename && (
                        <>
                            <code className="block break-all text-red-400">{keyUrl}</code>
                            <button
                                type="button"
                                onClick={() => copy(keyUrl, "Key URL")}
                                className="text-zinc-400 hover:text-white text-[11px] inline-flex items-center gap-1"
                            ><Copy className="w-3 h-3" /> Copy key URL</button>
                        </>
                    )}
                </div>

                <button
                    type="button"
                    onClick={ping}
                    disabled={busy}
                    data-testid="seo-indexnow-ping"
                    className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider px-5 py-3 rounded-sm flex items-center justify-center gap-2"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    {busy ? "Pinging…" : "Ping Bing & Yandex (IndexNow)"}
                </button>

                {pingResult && (
                    <div className={`rounded-sm border p-3 text-xs ${pingResult.ok ? "border-green-700/40 bg-green-900/20 text-green-300" : "border-amber-700/40 bg-amber-900/20 text-amber-300"}`}>
                        <div className="font-bold">
                            {pingResult.ok ? `Pinged successfully (HTTP ${pingResult.status_code})` : `IndexNow returned HTTP ${pingResult.status_code}`}
                        </div>
                        <div className="mt-1 text-zinc-400">
                            {pingResult.url_count} URLs sent for host <span className="text-white">{pingResult.host}</span>.
                        </div>
                        {pingResult.body_preview && (
                            <div className="mt-1 text-[11px] text-zinc-500 break-all">
                                Response: {pingResult.body_preview || "(empty body — that's normal for 200/202)"}
                            </div>
                        )}
                    </div>
                )}

                <p className="text-[11px] text-zinc-500 leading-relaxed">
                    Re-run this whenever you publish a new gallery job, article, or service. Bing/Yandex
                    typically index within hours of an IndexNow ping.
                </p>
            </div>

            <div className="rounded-sm border border-white/10 bg-zinc-900 p-5 sm:p-6 space-y-5" data-testid="seo-instructions">
                <div>
                    <h3 className="font-display font-black uppercase tracking-tight text-white text-xl flex items-center gap-2">
                        <ExternalLink className="w-5 h-5 text-red-500" /> One-time setup
                    </h3>
                    <p className="mt-1 text-sm text-zinc-400">
                        Do these once. After that, IndexNow handles Bing/Yandex on every push and Google
                        will recrawl your sitemap automatically.
                    </p>
                </div>

                <div className="space-y-4 text-sm">
                    <div className="rounded-sm border border-white/10 bg-zinc-950 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm">Step 1</span>
                            <span className="text-white font-bold">Google Search Console (HTML meta path)</span>
                        </div>
                        <ol className="text-zinc-300 text-xs space-y-1.5 list-decimal pl-4">
                            <li>Go to <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-red-400 underline">search.google.com/search-console</a></li>
                            <li><strong>Add property</strong> → choose <strong>URL prefix</strong> (NOT Domain) → enter <code>https://limitbreakperformance.com</code></li>
                            <li>Pick verification method <strong>HTML tag</strong>. It'll give you a string like <code>content="ABCD…1234"</code></li>
                            <li>Send me just that string. I'll paste it into the env var <code>REACT_APP_GOOGLE_SITE_VERIFICATION</code> and you redeploy</li>
                            <li>Click <strong>Verify</strong> → it works instantly</li>
                            <li>Once verified → Sitemaps → submit <code className="text-red-400">sitemap.xml</code></li>
                        </ol>
                    </div>

                    <div className="rounded-sm border border-white/10 bg-zinc-950 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm">Step 2</span>
                            <span className="text-white font-bold">Bing Webmaster Tools</span>
                        </div>
                        <ol className="text-zinc-300 text-xs space-y-1.5 list-decimal pl-4">
                            <li>Go to <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="text-red-400 underline">bing.com/webmasters</a></li>
                            <li>Sign in → click <strong>Import from Google Search Console</strong> — auto-imports verification + sitemap in 30 seconds</li>
                        </ol>
                    </div>

                    <div className="rounded-sm border border-white/10 bg-zinc-950 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm">Step 3</span>
                            <span className="text-white font-bold">Google Analytics 4 (optional)</span>
                        </div>
                        <ol className="text-zinc-300 text-xs space-y-1.5 list-decimal pl-4">
                            <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noreferrer" className="text-red-400 underline">analytics.google.com</a> → create a property for <code>limitbreakperformance.com</code></li>
                            <li>Copy the <strong>Measurement ID</strong> (looks like <code>G-XXXXXXXX</code>)</li>
                            <li>Send it to me — I'll paste it into <code>REACT_APP_GA4_MEASUREMENT_ID</code> and you redeploy</li>
                            <li>Page views, devices, and conversion sources start tracking automatically</li>
                        </ol>
                    </div>

                    <div className="rounded-sm border border-white/10 bg-zinc-950 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="bg-zinc-700 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm">Auto</span>
                            <span className="text-white font-bold">IndexNow + Schema markup</span>
                        </div>
                        <p className="text-zinc-300 text-xs">
                            Already wired. <strong>IndexNow</strong>: click "Ping Bing &amp; Yandex" any time you publish.{" "}
                            <strong>JSON-LD schema</strong> (LocalBusiness + AutoRepair) is injected on every page so once Google indexes you, your business shows up in search results with phone, service area, and city tags.
                        </p>
                    </div>

                    <div className="rounded-sm border border-amber-700/30 bg-amber-900/10 p-4 text-amber-200 text-xs">
                        <strong>Note:</strong> Google does not honor IndexNow. They use sitemap re-crawls (every 1-7 days) — submitting the sitemap once in Search Console is the only Google indexing step you need.
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Admin() {
    useSEO({ title: "Admin Dashboard" });

    const [authed, setAuthed] = useState(Boolean(getAdminToken()));
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [summary, setSummary] = useState({ referrers: [] });
    const [feedback, setFeedback] = useState([]);
    const [sources, setSources] = useState({ sources: [], total_with_source: 0, total_feedback: 0, overall_avg_rating: null });
    const [tab, setTab] = useState("referrals");
    // Top-level workspace switch: "ops" (Business Ops — leads/feedback/SEO/site)
    // or "content" (Content Studio — Photo/Reel/Video/Content Queue).
    // We auto-switch when the user picks a tab that lives in the other group.
    const [workspace, setWorkspace] = useState("ops");

    const refresh = async () => {
        if (!getAdminToken()) {
            setAuthed(false);
            return;
        }
        setLoading(true);
        try {
            const c = adminClient();
            const [r1, r2, r3, r4, r5] = await Promise.all([
                c.get("/admin/contacts"),
                c.get("/admin/referrals"),
                c.get("/admin/summary"),
                c.get("/admin/feedback"),
                c.get("/admin/sources"),
            ]);
            setContacts(r1.data);
            setReferrals(r2.data);
            setSummary(r3.data);
            setFeedback(r4.data);
            setSources(r5.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                clearAdminToken();
                setAuthed(false);
                toast.error("Session expired. Sign back in.");
            } else {
                toast.error("Couldn't load admin data.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authed) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authed]);

    const updateStatus = async (id, status) => {
        try {
            await adminClient().put(`/admin/referrals/${id}/status`, { status });
            toast.success("Updated.");
            refresh();
        } catch {
            toast.error("Couldn't update.");
        }
    };

    const updateAmount = async (id, rev_cash) => {
        try {
            await adminClient().put(`/admin/referrals/${id}/amount`, { rev_cash });
            toast.success("Rev Cash amount updated.");
            refresh();
        } catch {
            toast.error("Couldn't update amount.");
        }
    };

    const quickComplete = async (id, name) => {
        try {
            await adminClient().put(`/admin/referrals/${id}/status`, { status: "completed" });
            toast.success(`Marked completed — Rev Cash credited to ${name}.`);
            refresh();
        } catch {
            toast.error("Couldn't mark completed.");
        }
    };

    const quickPay = async (id, name) => {
        try {
            await adminClient().put(`/admin/referrals/${id}/status`, { status: "paid" });
            toast.success(`Marked paid out to ${name}.`);
            refresh();
        } catch {
            toast.error("Couldn't mark paid.");
        }
    };

    const logout = () => {
        clearAdminToken();
        setAuthed(false);
    };

    if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

    const totalUnpaid = summary.referrers.reduce((s, r) => s + (r.rev_cash_unpaid || 0), 0);

    return (
        <div className="lbp-container py-12 sm:py-16" data-testid="admin-dashboard">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">
                        Internal
                    </p>
                    <h1 className="font-display font-black uppercase tracking-tighter text-white text-3xl sm:text-4xl">
                        Admin Dashboard
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={refresh}
                        disabled={loading}
                        data-testid="admin-refresh-button"
                        className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wide"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
                    </button>
                    <button
                        onClick={logout}
                        data-testid="admin-logout-button"
                        className="inline-flex items-center gap-2 border border-red-600/40 text-red-400 hover:bg-red-600/10 px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wide"
                    >
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-4 gap-3">
                <StatCard icon={Inbox} label="Total Leads" value={contacts.length} />
                <StatCard icon={Users} label="Referrals" value={referrals.length} />
                <StatCard icon={Coins} label="Rev Cash Unpaid" value={`$${totalUnpaid}`} sub="Owed to referrers" />
                <StatCard icon={Star} label="Feedback" value={feedback.length} sub={sources.overall_avg_rating ? `Avg ${sources.overall_avg_rating}/5 ★` : "no ratings yet"} />
            </div>

            <div className="mt-10">
                {/* Top-level workspace switch */}
                <div className="inline-flex rounded-sm border border-white/15 overflow-hidden mb-3">
                    {[
                        { k: "ops", label: "Business Ops" },
                        { k: "content", label: "Content Studio" },
                    ].map((w) => (
                        <button
                            key={w.k}
                            type="button"
                            data-testid={`admin-workspace-${w.k}`}
                            onClick={() => {
                                setWorkspace(w.k);
                                // Jump to the first tab of the chosen workspace.
                                setTab(w.k === "content" ? "photo_studio" : "referrals");
                            }}
                            className={`px-4 py-2 text-[11px] font-display font-bold uppercase tracking-widest ${
                                workspace === w.k
                                    ? "bg-red-600 text-white"
                                    : "bg-zinc-900 text-zinc-400 hover:text-white"
                            }`}
                        >
                            {w.label}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap gap-2 border-b border-white/10">
                    {workspace === "content" && <Link to="/admin/instagram" data-testid="admin-instagram-jobs-link" className="px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-red-400 hover:text-red-300 transition-colors">Instagram Jobs</Link>}
                    {(workspace === "content"
                        ? [
                            { k: "photo_studio", label: "Photo Studio" },
                            { k: "reel_studio", label: "Reel Studio" },
                            { k: "video_studio", label: "Video Studio" },
                            { k: "video_enhancer", label: "Enhance Video" },
                            { k: "content_queue", label: "Content Queue" },
                            { k: "file_converter", label: "Converter → PNG" },
                        ]
                        : [
                            { k: "referrals", label: "Referrals" },
                            { k: "rev_cash", label: "Rev Cash by Referrer" },
                            { k: "leads", label: "All Leads" },
                            { k: "feedback", label: "Feedback" },
                            { k: "sources", label: "Where People Found Us" },
                            { k: "seo", label: "SEO" },
                            { k: "site_editor", label: "Site Editor" },
                        ]
                    ).map((t) => (
                        <button
                            key={t.k}
                            data-testid={`admin-tab-${t.k}`}
                            onClick={() => setTab(t.k)}
                            className={`px-4 py-2.5 text-sm font-semibold uppercase tracking-wider border-b-2 -mb-px whitespace-nowrap ${
                                tab === t.k
                                    ? "border-red-600 text-white"
                                    : "border-transparent text-zinc-400 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {tab === "referrals" && (
                <div className="mt-6 overflow-x-auto rounded-sm border border-white/10">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-widest">
                            <tr>
                                <th className="text-left px-4 py-3">When</th>
                                <th className="text-left px-4 py-3">Referrer</th>
                                <th className="text-left px-4 py-3">Friend</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Rev Cash</th>
                                <th className="text-left px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5 bg-zinc-950">
                            {referrals.length === 0 && (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-zinc-500">No referrals yet.</td></tr>
                            )}
                            {referrals.map((r) => (
                                <tr key={r.id} data-testid={`admin-referral-row-${r.id}`}>
                                    <td className="px-4 py-3 text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 text-white">
                                        <div className="font-semibold">{r.referrer_name}</div>
                                        <a href={`sms:${r.referrer_phone}`} className="text-xs text-zinc-400 hover:text-red-500 inline-flex items-center gap-1">
                                            <MessageSquareText className="w-3 h-3" />{r.referrer_phone}
                                        </a>
                                    </td>
                                    <td className="px-4 py-3 text-white">
                                        <div className="font-semibold">{r.friend_name || "—"}</div>
                                        <a href={`sms:${r.friend_phone}`} className="text-xs text-zinc-400 hover:text-red-500 inline-flex items-center gap-1">
                                            <Phone className="w-3 h-3" />{r.friend_phone}
                                        </a>
                                    </td>
                                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="number"
                                            min="0"
                                            defaultValue={r.rev_cash}
                                            onBlur={(e) => {
                                                const v = parseInt(e.target.value, 10);
                                                if (!isNaN(v) && v !== r.rev_cash) updateAmount(r.id, v);
                                            }}
                                            data-testid={`admin-referral-amount-${r.id}`}
                                            className="w-20 bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm text-white font-display font-bold focus:outline-none focus:ring-2 focus:ring-red-600"
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1.5">
                                            {r.status !== "completed" && r.status !== "paid" && (
                                                <button
                                                    onClick={() => quickComplete(r.id, r.referrer_name)}
                                                    data-testid={`admin-quick-complete-${r.id}`}
                                                    className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wide"
                                                    title={`Mark completed — credits $${r.rev_cash} to ${r.referrer_name}`}
                                                >
                                                    + Credit ${r.rev_cash}
                                                </button>
                                            )}
                                            {r.status === "completed" && (
                                                <button
                                                    onClick={() => quickPay(r.id, r.referrer_name)}
                                                    data-testid={`admin-quick-pay-${r.id}`}
                                                    className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wide"
                                                    title={`Mark paid out — clears $${r.rev_cash} from balance`}
                                                >
                                                    Mark Paid
                                                </button>
                                            )}
                                            <select
                                                value={r.status}
                                                onChange={(e) => updateStatus(r.id, e.target.value)}
                                                data-testid={`admin-referral-status-${r.id}`}
                                                className="bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white"
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="quote_requested">Quote Requested</option>
                                                <option value="completed">Completed</option>
                                                <option value="paid">Paid</option>
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "rev_cash" && (
                <div className="mt-6 overflow-x-auto rounded-sm border border-white/10">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-widest">
                            <tr>
                                <th className="text-left px-4 py-3">Referrer</th>
                                <th className="text-left px-4 py-3">Phone</th>
                                <th className="text-left px-4 py-3">Total Refs</th>
                                <th className="text-left px-4 py-3">Completed</th>
                                <th className="text-left px-4 py-3">Paid</th>
                                <th className="text-left px-4 py-3">Unpaid Rev$</th>
                                <th className="text-left px-4 py-3">Lifetime Rev$</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5 bg-zinc-950">
                            {summary.referrers.length === 0 && (
                                <tr><td colSpan="7" className="px-4 py-8 text-center text-zinc-500">No referrers yet.</td></tr>
                            )}
                            {summary.referrers.map((r, i) => (
                                <tr key={i}>
                                    <td className="px-4 py-3 text-white font-semibold">{r.referrer_name}</td>
                                    <td className="px-4 py-3 text-zinc-400">
                                        <a href={`sms:${r.referrer_phone}`} className="hover:text-red-500">{r.referrer_phone}</a>
                                    </td>
                                    <td className="px-4 py-3 text-zinc-300">{r.total_referrals}</td>
                                    <td className="px-4 py-3 text-zinc-300">{r.completed}</td>
                                    <td className="px-4 py-3 text-zinc-300">{r.paid}</td>
                                    <td className="px-4 py-3 font-display font-bold text-amber-400">${r.rev_cash_unpaid}</td>
                                    <td className="px-4 py-3 font-display font-bold text-green-400">${r.rev_cash_earned}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "leads" && (
                <div className="mt-6 overflow-x-auto rounded-sm border border-white/10">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-widest">
                            <tr>
                                <th className="text-left px-4 py-3">When</th>
                                <th className="text-left px-4 py-3">Name</th>
                                <th className="text-left px-4 py-3">Phone</th>
                                <th className="text-left px-4 py-3">Vehicle</th>
                                <th className="text-left px-4 py-3">Service</th>
                                <th className="text-left px-4 py-3">Message</th>
                                <th className="text-left px-4 py-3">Source</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5 bg-zinc-950">
                            {contacts.length === 0 && (
                                <tr><td colSpan="7" className="px-4 py-8 text-center text-zinc-500">No leads yet.</td></tr>
                            )}
                            {contacts.map((c) => (
                                <tr key={c.id}>
                                    <td className="px-4 py-3 text-zinc-400">{new Date(c.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 text-white font-semibold">{c.name}</td>
                                    <td className="px-4 py-3"><a href={`sms:${c.phone}`} className="text-zinc-300 hover:text-red-500">{c.phone}</a></td>
                                    <td className="px-4 py-3 text-zinc-400">{c.vehicle || "—"}</td>
                                    <td className="px-4 py-3 text-zinc-400">{c.service || "—"}</td>
                                    <td className="px-4 py-3 text-zinc-400 max-w-xs truncate">{c.message || "—"}</td>
                                    <td className="px-4 py-3">
                                        {c.referral_id ? (
                                            <span className="text-[10px] uppercase tracking-widest font-bold text-red-400">Referral</span>
                                        ) : (
                                            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Direct</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "feedback" && (
                <div className="mt-6 grid gap-3" data-testid="admin-feedback-list">
                    {feedback.length === 0 && (
                        <div className="rounded-sm border border-white/10 bg-zinc-900 p-8 text-center text-zinc-500">
                            No feedback yet.
                        </div>
                    )}
                    {feedback.map((f) => (
                        <div
                            key={f.id}
                            data-testid={`admin-feedback-${f.id}`}
                            className="rounded-sm border border-white/10 bg-zinc-900 p-5"
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-white font-semibold">
                                            {f.is_anonymous ? "Anonymous" : (f.name || "No name")}
                                        </span>
                                        {f.rating ? (
                                            <span className="inline-flex items-center gap-0.5 text-amber-400 text-sm">
                                                {"★".repeat(f.rating)}
                                                <span className="text-zinc-600">{"★".repeat(5 - f.rating)}</span>
                                                <span className="ml-1 text-xs text-zinc-500">{f.rating}/5</span>
                                            </span>
                                        ) : null}
                                        {f.heard_from && (
                                            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-300 border border-white/10">
                                                {f.heard_from}
                                                {f.heard_from === "Other" && f.heard_from_other ? `: ${f.heard_from_other}` : ""}
                                            </span>
                                        )}
                                        {f.can_share ? (
                                            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-green-600/20 text-green-300 border border-green-600/40">
                                                OK to publish
                                            </span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-400 border border-white/10">
                                                Private
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-zinc-500">
                                        <span>{new Date(f.created_at).toLocaleString()}</span>
                                        {f.email && <a href={`mailto:${f.email}`} className="hover:text-red-500">{f.email}</a>}
                                        {f.phone && <a href={`sms:${f.phone}`} className="hover:text-red-500">{f.phone}</a>}
                                    </div>
                                </div>
                            </div>
                            <p className="mt-4 text-zinc-200 leading-relaxed whitespace-pre-wrap">
                                {f.feedback_text}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            {tab === "sources" && (
                <div className="mt-6 grid gap-4" data-testid="admin-sources-panel">
                    <div className="grid sm:grid-cols-3 gap-3">
                        <StatCard icon={Inbox} label="Total Feedback" value={sources.total_feedback} />
                        <StatCard icon={BarChart3} label="With Source" value={sources.total_with_source} sub="Includes 'Where heard from'" />
                        <StatCard icon={Star} label="Avg Rating" value={sources.overall_avg_rating ? `${sources.overall_avg_rating}/5` : "—"} />
                    </div>

                    <div className="rounded-sm border border-white/10 bg-zinc-900 p-5 sm:p-6">
                        <h3 className="font-display font-bold uppercase tracking-tight text-white text-lg">
                            Where People Found Us
                        </h3>
                        {sources.sources.length === 0 ? (
                            <p className="mt-4 text-sm text-zinc-500">No source data yet.</p>
                        ) : (
                            <div className="mt-5 space-y-3">
                                {sources.sources.map((s) => (
                                    <div key={s.source} data-testid={`admin-source-${s.source.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-white font-semibold">{s.source}</span>
                                            <span className="text-zinc-400">
                                                <span className="font-display font-bold text-white">{s.count}</span>
                                                <span className="ml-2 text-xs text-zinc-500">{s.percent}%</span>
                                                {s.avg_rating ? (
                                                    <span className="ml-3 text-xs text-amber-400">★ {s.avg_rating}</span>
                                                ) : null}
                                            </span>
                                        </div>
                                        <div className="mt-1.5 h-2 rounded-sm bg-zinc-950 overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-red-600 to-red-500"
                                                style={{ width: `${s.percent}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <p className="mt-5 text-[11px] text-zinc-500">
                            Tip: ask every customer to drop quick feedback after a job — this graph becomes your marketing roadmap (where to spend time, where to advertise).
                        </p>
                    </div>
                </div>
            )}

            {tab === "photo_studio" && <PhotoStudio />}
            {tab === "reel_studio" && <ReelStudio />}
            {tab === "video_studio" && <VideoStudio />}
            {tab === "video_enhancer" && <VideoEnhancer />}
            {tab === "content_queue" && <ContentQueue />}
            {tab === "file_converter" && <FileConverter />}
            {tab === "seo" && <SeoStudio />}
            {tab === "site_editor" && <SiteEditor />}
        </div>
    );
}
